/**
 * Utilities for handling dynamic `refDocPath` template variables in form
 * field configurations.
 *
 * A dynamic path contains `${field_key}` placeholders that are resolved at
 * runtime against the current form field values (from `PharmaFormFieldsContext`).
 *
 * @module refDocPathUtils
 */

import { FirestoreService } from "../../../api/firebase/firestoreService";
import { logError } from "../../../utils/customLogger";

/** Global-flag regex used by `extractTemplateKeys` and `resolveTemplatePath`. */
const TEMPLATE_VAR_REGEX = /\$\{([^}]+)\}/g;
/** Non-global regex for simple boolean `.test()` checks (avoids `lastIndex` statefulness). */
const TEMPLATE_VAR_TEST = /\$\{([^}]+)\}/;

/**
 * Returns `true` if the given path contains at least one `${…}` template variable.
 *
 * @param path - A Firestore document path string.
 */
export const isDynamicPath = (path: string): boolean =>
  TEMPLATE_VAR_TEST.test(path);

/**
 * Extracts all template variable keys from a path.
 *
 * @example
 * ```ts
 * extractTemplateKeys("/formOptions/${drug_name}/sub/${category}")
 * // => ["drug_name", "category"]
 * ```
 *
 * @param path - A Firestore document path containing `${…}` placeholders.
 * @returns An array of field keys referenced in the path.
 */
export const extractTemplateKeys = (path: string): string[] => {
  const keys: string[] = [];
  let match: RegExpExecArray | null;
  const regex = new RegExp(TEMPLATE_VAR_REGEX);
  while ((match = regex.exec(path)) !== null) {
    keys.push(match[1]);
  }
  return keys;
};

/**
 * Replaces every `${field_key}` in the path with the corresponding
 * `filledValue` from `formFieldsData`.
 *
 * @param path           - The template path to resolve.
 * @param formFieldsData - Current form field values keyed by field key.
 * @returns The fully resolved path, or `null` if any variable could not be resolved.
 */
export const resolveTemplatePath = (
  path: string,
  formFieldsData: Record<string, any>,
): string | null => {
  let resolved = path;
  let allResolved = true;

  resolved = resolved.replace(TEMPLATE_VAR_REGEX, (_match, key: string) => {
    const value = formFieldsData[key]?.filledValue;
    if (value === undefined || value === null || value === "") {
      allResolved = false;
      return "";
    }
    return String(value);
  });

  return allResolved ? resolved : null;
};

/**
 * Fetches a Firestore document at the given path and extracts an options array
 * from the specified field (defaults to `"data"`).
 *
 * Handles two common data shapes:
 * - **Simple string arrays** — each string becomes both `label` and `value`.
 * - **Object arrays** — maps common key names (`label`/`name`/`text`,
 *   `value`/`id`) into `{ label, value }` pairs.
 *
 * @param resolvedPath - Fully resolved (no template variables) Firestore document path.
 * @param fieldName    - Field name inside the document holding the options array.
 *                       Defaults to `"data"`.
 * @returns A normalised `{ label, value }[]` array, or `[]` on error.
 */
export const fetchOptionsFromPath = async (
  resolvedPath: string,
  fieldName?: string,
): Promise<{ label: string; value: string }[]> => {
  try {
    const doc =
      await FirestoreService.getDocument<Record<string, any>>(resolvedPath);
    const raw = doc[fieldName || "data"];
    if (!Array.isArray(raw)) return [];

    return raw.map((item) => {
      if (typeof item === "string") {
        return { label: item, value: item };
      }
      return {
        label: String(item.label ?? item.name ?? item.text ?? item),
        value: String(item.value ?? item.id ?? item.label ?? item),
      };
    });
  } catch (error) {
    logError(
      error as Error,
      `Error fetching options from path: ${resolvedPath}`,
    );
    return [];
  }
};
